import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  InputLabel,
  makeStyles,
  TextField,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import { green } from '@material-ui/core/colors';
import { Formik } from 'formik';
import * as Yup from 'yup';
import CustomDialog from 'src/app/components/CustomDialog';
import { messageToastType_const } from 'src/app/constant/config';
import { getListCustomer } from 'src/features/customerSlice';
import { showGlobalToast } from 'src/features/uiSlice';
import { updateCustomerAccount } from 'src/features/userSlice';

function EditCustomerAccount({ open, onClose, customer, currentParams }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toast = (message, type) =>
    dispatch(showGlobalToast({ message, type }));

  const handleSubmit = async values => {
    const phone = values.phone.trim();
    const username = values.username.trim();
    const fullName = values.full_name.trim();
    const payload = { userId: customer.id };
    if (fullName !== (customer.full_name || '')) payload.full_name = fullName;
    if (phone !== (customer.phone || '')) payload.phone = phone;
    if (username && username !== (customer.username || '')) payload.username = username;

    if (!payload.phone && !payload.username && !payload.full_name) {
      onClose();
      return;
    }

    setIsSubmitting(true);
    const action = await dispatch(updateCustomerAccount(payload));

    if (updateCustomerAccount.rejected.match(action)) {
      setIsSubmitting(false);
      toast(
        action.payload?.message || 'Cập nhật thông tin khách hàng thất bại',
        messageToastType_const.error
      );
      return;
    }

    const refetch = await dispatch(getListCustomer(currentParams));
    const refreshed = refetch.payload?.payload?.data?.find(
      item => item.id === customer.id
    );
    setIsSubmitting(false);

    if (payload.phone && refreshed && refreshed.phone !== payload.phone) {
      toast(
        'Số điện thoại đã tồn tại, không thể cập nhật số điện thoại cho khách hàng',
        messageToastType_const.warning
      );
    } else {
      toast('Cập nhật thông tin khách hàng thành công', messageToastType_const.success);
    }
    onClose();
  };

  return (
    <CustomDialog
      title="Chỉnh sửa thông tin khách hàng"
      open={open}
      onClose={onClose}
    >
      <div>
        <Formik
          initialValues={{
            full_name: customer?.full_name || '',
            phone: customer?.phone || '',
            username: customer?.username || ''
          }}
          validationSchema={Yup.object().shape({
            full_name: Yup.string()
              .trim()
              .max(255)
              .required('Tên khách hàng không được để trống'),
            phone: Yup.string()
              .matches(
                /^(84|0[3|5|7|8|9])+([0-9]{8})$/,
                'Số điện thoại chưa đúng định dạng'
              )
              .required('Số điện thoại không được để trống'),
            username: Yup.string()
              .max(100)
              .matches(/^\S+$/, 'Tên đăng nhập không được chứa khoảng trắng')
              .matches(
                /^[a-zA-Z0-9_]+$/,
                'Tên đăng nhập không được chứa ký tự đặc biệt'
              )
          })}
          onSubmit={handleSubmit}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            touched,
            values
          }) => (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item md={6} xs={12}>
                  <InputLabel>Tên khách hàng <span style={{ color: 'red' }}>*</span></InputLabel>
                  <TextField
                    error={Boolean(touched.full_name && errors.full_name)}
                    fullWidth
                    className="input-no-fieldset"
                    helperText={touched.full_name && errors.full_name}
                    margin="normal"
                    size="small"
                    name="full_name"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.full_name}
                    variant="outlined"
                    placeholder="Nhập tên khách hàng"
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <InputLabel>Số điện thoại <span style={{ color: 'red' }}>*</span></InputLabel>
                  <TextField
                    error={Boolean(touched.phone && errors.phone)}
                    fullWidth
                    className="input-no-fieldset"
                    helperText={touched.phone && errors.phone}
                    margin="normal"
                    size="small"
                    name="phone"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.phone}
                    variant="outlined"
                    placeholder="Nhập số điện thoại"
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <InputLabel>Tên đăng nhập</InputLabel>
                  <TextField
                    error={Boolean(touched.username && errors.username)}
                    fullWidth
                    className="input-no-fieldset"
                    helperText={touched.username && errors.username}
                    margin="normal"
                    size="small"
                    name="username"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.username}
                    variant="outlined"
                    placeholder="Nhập tên đăng nhập"
                  />
                </Grid>
              </Grid>
              <Box my={3}>
                <div className={classes.groupButtonSubmit}>
                  <div className={classes.wrapper}>
                    <Button
                      style={{
                        ...(isMobile && { maxWidth: 90 }),
                        marginRight: '10px',
                        textTranform: 'none !important'
                      }}
                      onClick={() => onClose()}
                      className="btn-main btn-plain mx-3"
                      color="primary"
                      size="large"
                      variant="contained"
                      disabled={isSubmitting}
                    >
                      Thoát
                    </Button>
                    <Button
                      style={{
                        ...(isMobile && { maxWidth: 120 }),
                        marginRight: '10px',
                        textTranform: 'none !important'
                      }}
                      className="btn-main mx-3"
                      color="primary"
                      size="large"
                      type="submit"
                      variant="contained"
                      disabled={isSubmitting}
                    >
                      Lưu lại
                    </Button>
                    {isSubmitting && (
                      <CircularProgress
                        size={24}
                        className={classes.buttonProgress}
                      />
                    )}
                  </div>
                </div>
              </Box>
            </form>
          )}
        </Formik>
      </div>
    </CustomDialog>
  );
}

const useStyles = makeStyles(() => ({
  groupButtonSubmit: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '15px'
  },
  wrapper: {
    position: 'relative'
  },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12
  }
}));

export default React.memo(EditCustomerAccount);
